import moment from "moment";
import { CostBreakupReportProps } from "../types";
import { formatCurrency, hexToGrayscale } from "../functions";

const buildScript = (functionNames: string[], functionDefinitions: string[]) => {
    return `
    <script type="text/javascript">
      google.charts.load("current", {packages:["corechart"]});
      google.charts.setOnLoadCallback(drawCharts);
    
      function drawCharts() {
        ${functionNames.map(fnName => `${fnName}()`).join(';')}
      };

      ${functionDefinitions.map(fnDef => `${fnDef}`).join('')}

    </script>
    `;
}

function processBOMDetailsFlatten(
    bomDetails: any,
    level = "1",
    base_currency: string,
    hasNotes: boolean,
    useMBFormatter: boolean,
    normalized: {
        isNormalized: boolean;
        final_price: number;
        total_first_level: number;
    },
    parentQty = 0,
    total_bom_cost = 0
) {
    const flattenedList = [];

    let bom_pricing_cost = 0;

    let bom_unit_price = 0;

    if (level === "1" && normalized.isNormalized) {
        bom_pricing_cost = normalized.final_price;

        bom_unit_price = Number((normalized.final_price / parentQty).toFixed(2));
    } else if (level === "1") {
        bom_pricing_cost = total_bom_cost;

        bom_unit_price = Number((total_bom_cost / parentQty).toFixed(2));
    } else if (normalized.isNormalized) {
        bom_pricing_cost =
            ((bomDetails?.final_total_cost_in_pricing_currency?.cost || 0) /
                normalized.total_first_level) *
            normalized.final_price;

        bom_unit_price = bom_pricing_cost / (bomDetails.total_quantity || 1);
    } else {
        bom_pricing_cost =
            bomDetails?.final_total_cost_in_pricing_currency?.cost || 0;

        bom_unit_price = bomDetails?.unit_cost_in_pricing_currency?.cost || 0;
    }

    // Create the parent object
    const parent = {
        "level": `${level}`,
        "item_number": bomDetails.sequence_id,
        "version": `r${bomDetails.version}`,
        "description": `${bomDetails.short_description}${hasNotes && bomDetails?.notes ? `\nNotes: ${bomDetails.notes}` : ""}`,
        "unitCost": formatCurrency(
            bom_unit_price,
            base_currency,
            useMBFormatter,
            false
        ),
        "qty": level === "1" ? parentQty : bomDetails.total_quantity || 1,
        "totalCost": formatCurrency(
            bom_pricing_cost,
            base_currency,
            useMBFormatter,
            false
        ),
    };

    const assembly_process_costs =
        bomDetails?.assembly_process_costs?.map((a, idx) => {
            return {
                "level": "",
                "item_number": idx + 1,
                "version": `N/A`,
                "description": `${a.proc_name}`,
                "unitCost": formatCurrency(
                    a.unit_cost_in_pricing_currency?.cost || 0,
                    base_currency,
                    useMBFormatter,
                    false
                ),
                "qty": a.quantity || 1,
                "totalCost": formatCurrency(
                    a?.final_total_cost_in_pricing_currency?.cost || 0,
                    base_currency,
                    useMBFormatter,
                    false
                ),
            };
        }) || [];

    // Array to hold the immediate children of this parent
    const immediateChildren = [];

    // Process children BOMs recursively
    for (let i = 0; i < (bomDetails?.children_costs || []).length; i++) {
        const childBOM = bomDetails.children_costs[i];
        const childBOMDetails = processBOMDetailsFlatten(
            childBOM,
            `${level}.${i + 1}`,
            base_currency,
            hasNotes,
            useMBFormatter,
            normalized
        );
        immediateChildren.push(childBOMDetails[0].parent); // Add the immediate child to the array
        flattenedList.push(...childBOMDetails); // Add the entire flattened structure of the child
    }

    // Process material children (non-recursive)
    for (let i = 0; i < (bomDetails?.material_costs || []).length; i++) {
        const material = bomDetails.material_costs[i];

        let material_pricing_cost =
            material?.final_total_cost_in_pricing_currency?.cost ||
            material.total_cost?.cost ||
            0;

        let material_unit_price =
            material?.unit_cost_in_pricing_currency?.cost || 0;

        if (normalized.isNormalized) {
            material_pricing_cost =
                (material_pricing_cost / normalized.total_first_level) *
                normalized.final_price;

            material_unit_price =
                material_pricing_cost / (material.total_quantity || 1);
        }

        const materialChild = {
            parent: {
                "level": `${level}.${(bomDetails.children_costs?.length || 0) + i + 1}`,
                "item_number": material.sequence_id,
                "version": `r${material?.version}`,
                "description": `${material?.short_description}${hasNotes && material?.notes ? `\nNotes: ${material.notes}` : ""}`,
                "unitCost": formatCurrency(
                    material_unit_price,
                    base_currency,
                    useMBFormatter,
                    false
                ),
                "qty": material.total_quantity || 1,
                "totalCost": formatCurrency(
                    material_pricing_cost,
                    base_currency,
                    useMBFormatter,
                    false
                )
            },
            children: [],
        };

        // Add the material child to immediateChildren and flattenedList
        immediateChildren.push(materialChild.parent);
        flattenedList.push(materialChild);
    }

    // Add the parent to the flattened list
    flattenedList.unshift({
        parent,
        assembly_process_costs,
        children: immediateChildren,
        totalParentCost:
            level === "1"
                ? total_bom_cost
                : bomDetails?.final_total_cost_in_pricing_currency?.cost || 0,
    });

    // Return the flattened list
    return flattenedList;
}

const TOTAL_COST_INITIAL_WIDTH = 38;

export const buildPricingReportHTML = async ({
    headerTitle,
    orientation,
    paperSize,
    bomDetails,
    pricingId,
    configState,
}: CostBreakupReportProps): Promise<{ body: string, script: string }> => {

    let body: string = '';
    const functionNames: string[] = [];
    const functionDefinitions: string[] = [];

    const pricing_config = bomDetails?.pricing_config;
    const base_currency = pricing_config?.exchange_rates?.base_currency ?? "USD";

    const allLevels = configState?.levelLimit?.value === "AllLevelbreakup";
    const noBreakup = configState?.levelLimit?.value === "Nobreakup";
    const commercial = configState?.reportType?.value === "Commercial Only";
    const technical = configState?.reportType?.value === "Technical Only";
    const technoCommercial = configState?.reportType?.value === "Techno Commercial";
    const hasOtherCost = configState?.hasOtherCost;
    const hasAdjustment = configState?.hasAdjustment;
    const hasTax = configState?.hasTax;
    const hideCostGroup = configState?.onlyBOM;
    const header_color = configState?.tableHeaderColor || "#d1c4e9";
    const hasNotes = configState?.hasNotes;
    const useMBFormatter = configState?.useMBFormatter;
    const isNormalized = configState?.isNormalized ?? false;
    const hasCGI = configState?.hasCostGroupItems ?? false;

    // Chart IDs
    const pricingDonoutChartIndexPage: string = 'pricingDonoutChartIndexPage';

    // First Page
    body += `<div class="page-break relative ">
        <h1 class=" text-2xl font-bold">Pricing Report: <span class=" text-neutral-500">${pricing_config?.pricing_name ?? ""}</span></h1>

        <div class=" flex flex-col gap-4 mt-6">
            <div class=" flex flex-row items-center justify-between">
                <p class=" text-xl">Pricing ID: ${pricingId}</p>
                <p class=" text-xl">Calculated by: ${bomDetails.created_user}</p>
            </div>

            <div class=" flex flex-row items-center justify-between">
                <p class=" text-xl">Root Item: ${bomDetails.calculated_cost?.sequence_id}-r${bomDetails.calculated_cost?.version || 0}</p>
                <p class=" text-xl">Calculated at: ${moment(bomDetails.created_time).format("ll")}</p>
            </div>

            <p class=" text-xl">Currency: ${base_currency}</p>

            <p class=" text-xl">Remarks: ${pricing_config.remarks}</p>
        </div>

        <div>
            <div class="mt-5">                
                    <div class=" px-2 py-2 text-xl w-full flex justify-between border-b-2 border-black bg-[${header_color}] text-[${hexToGrayscale(header_color) > 150 ? "#000000" : "#FFFFFF"}]">
                        <td class="pr-2 py-2 text-xl text-left break-all w-[16rem]">Total Cost (${pricing_config?.margin_ratio ? 'including margin' : 'before tax'})</td>
                        <p>${formatCurrency(bomDetails?.gross_cost_after_margin ?? 0, base_currency, useMBFormatter)}</p>
                    </div>
            </div>
        </div>

        ${(!isNormalized && Object.keys(bomDetails || {}).length > 0) ? `<div id="${pricingDonoutChartIndexPage}" class=" mt-7" style="width: 550px; height: 400px;"></div>` : ``}

    </div>`;

    // Building script for index page google chart
    if (!isNormalized && Object.keys(bomDetails || {}).length > 0) {
        functionNames.push(pricingDonoutChartIndexPage);

        // Master Data Manipulation
        const includeMasterFields = [
            'total_bom_cost',
            'total_other_cost',
            'total_discount',
            'total_tax'
        ];

        if (bomDetails?.margin_amount) {
            includeMasterFields.push("margin_amount");
        }

        const masterLabels: Record<any, string> = {
            'total_bom_cost': `Major Items`,
            'total_other_cost': `Other Items`,
            'total_discount': `Adjustments`,
            'total_tax': `Taxes`,
        };

        if (bomDetails?.margin_amount) {
            masterLabels["margin_amount"] = "Margin";
        }

        const masterData = [
            ["Pricing Breakup", "Value"],
            ...(includeMasterFields.map((key: string) => {
                if (key === includeMasterFields[0]) {
                    // eslint-disable-next-line
                    return [`${masterLabels[key]} (${formatCurrency(Number((Number(eval(`bomDetails?.${key}`)) + bomDetails?.total_cost_group_cost).toFixed(2)), base_currency, false)})`, Number((Number(eval(`bomDetails?.${key}`)) + bomDetails?.total_cost_group_cost).toFixed(2))]
                }
                // eslint-disable-next-line
                return [`${masterLabels[key]} (${formatCurrency(Number(Math.abs(eval(`bomDetails?.${key}`)).toFixed(2)), base_currency, false)})`, Number(Math.abs(eval(`bomDetails?.${key}`)).toFixed(2))]
            }) ?? [])
        ]

        functionDefinitions.push(`
            function ${pricingDonoutChartIndexPage}() {
                
                var data = google.visualization.arrayToDataTable(${JSON.stringify(masterData)});

                var options = {
                    pieHole: 0.4,
                    is3D: false,
                    backgroundColor: 'transparent',
                    legend: {
                        position: 'right',
                        alignment: 'center',
                        textStyle: {
                            fontSize: 10
                        },
                        // Ensure full text display
                        maxLines: 3,
                        width: '100%'
                    },
                    textColor: 'red',
                    title: 'Master Pricing(${base_currency})',
                    titleTextStyle: {
                        fontSize: 16,
                        bold: true
                    },
                    chartArea: {
                        top: '15%',
                        width: '100%',
                        height: '70%',
                    },
                    width: '100%',
                    height: '100%'
                };

                var chart = new google.visualization.PieChart(document.getElementById('${pricingDonoutChartIndexPage}'));
                chart.draw(data, options);
            }
        `);
    }

    // Master Pricing Summary
    let total_first_level = 0;

    total_first_level += bomDetails?.calculated_cost?.children_costs?.reduce((acc, curr) => acc + curr?.final_total_cost_in_pricing_currency?.cost || 0, 0);
    total_first_level += bomDetails?.calculated_cost?.material_costs?.reduce((acc, curr) => acc + curr?.final_total_cost_in_pricing_currency?.cost || 0, 0);

    const result = processBOMDetailsFlatten(
        bomDetails?.calculated_cost,
        "1",
        base_currency,
        hasNotes,
        useMBFormatter,
        {
            isNormalized,
            final_price: bomDetails?.final_price ?? 0,
            total_first_level,
        },
        bomDetails?.pricing_config?.bom_cost_quantity,
        bomDetails?.total_bom_cost
    ) ?? [];


    body += `<div class="page-break relative">
    <div>
        ${headerTitle ? `<h1 class=" text-2xl font-bold">${headerTitle}</h1>` : ``}
        ${result.length > 0 ? `<p class=" text-neutral-500">
        L${result?.[0]?.parent?.column_content?.find((c) => c.field === "level")?.content ?? ""} - 
        ${bomDetails?.calculated_cost?.short_description?.split("\nNotes:")?.[0] ?? ""} 
        (${bomDetails?.calculated_cost?.sequence_id ?? ""}-r${bomDetails?.calculated_cost?.version ?? 0})
        </p>`: ``}
    </div>
    `;

    if (result.length > 0) {

        let parentBOMRows = [result?.[0]?.parent];

        body += `
            <div>
                <table class="table-fixed mt-5 p-3 pl-6">
                    <thead>
                        <tr class="bg-[${header_color}] text-[${hexToGrayscale(header_color) > 150 ? "#000000" : "#FFFFFF"}] ">
                            <th class="pl-2 py-2 text-md text-left break-all w-[4rem]">Level</th>
                            <th class="pl-2 py-2 text-md text-left break-all w-[9rem]">Item No.</th>
                            <th class="pl-2 py-2 text-md text-left break-all w-[3rem]">Rev.</th>
                            <th class="pl-2 py-2 text-md text-left break-all w-[16rem]">Description</th>
                            ${!(technical || technoCommercial) ? `<th class="px-2 py-2 text-md text-right break-all w-[13rem]" >Unit Cost (${base_currency})</th>` : ``}                        
                            <th class="px-2 py-2 text-md text-right break-all w-[6rem]">Quantity</th>
                            ${!(technical || technoCommercial) ? `<th class="px-2 py-2 text-md text-right break-all w-[13rem]">Price (${base_currency})</th>` : ``}
                        </tr>
                    </thead>

                    <tbody>
                        ${parentBOMRows?.map((row, idx) => {
            return `
                            <tr class="${idx % 2 === 0 ? 'bg-gray-50' : 'bg-gray-100'}">
                                <td class="px-2 py-2 text-md">${row.level}</td>
                                <td class="px-2 py-2 text-md">${row.item_number}</td>
                                <td class="px-2 py-2 text-md">${row.version}</td>
                                <td class="px-2 py-2 text-md">${row.description?.replaceAll('\n', '<br>') ?? "-"}</td>
                                ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right">${row.unitCost}</td>` : ``}
                                <td class="px-2 py-2 text-md text-right">${row.qty}</td>
                                ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right">${row.totalCost}</td>` : ``}
                            </tr>
                            `
        }).join('') ?? ''}                    
                    </tbody>
                </table>
            </div>
        `;

        if (!noBreakup) {
            if (result?.[0]?.assembly_process_costs?.length > 0 && !isNormalized) {

                let assembly_process_costs = result?.[0]?.assembly_process_costs ?? [];

                body += `             
            <p class="mt-5 underline" >Overhead / Activity Breakup</p> 
            
            <div>
                <table class="table-fixed mt-5 p-3 pl-6">
                    <thead></thead>

                    <tbody>
                        ${assembly_process_costs?.map((row, idx) => {
                    return `
                            <tr class="${idx % 2 === 0 ? 'bg-gray-50' : 'bg-gray-100'}">
                                <td class="px-2 py-2 text-md text-left break-all w-[4rem]">${row.level}</td>
                                <td class="px-2 py-2 text-md text-left break-all w-[9rem]">${row.item_number}</td>
                                <td class="px-2 py-2 text-md break-all w-[3rem]">${row.version}</td>
                                <td class="px-2 py-2 text-md text-left break-all w-[16rem]">${row.description?.replaceAll('\n', '<br>') ?? "-"}</td>
                                ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right break-all w-[13rem]">${row.unitCost}</td>` : ``}
                                <td class="px-2 py-2 text-md text-right break-all w-[6rem]">${row.qty}</td>
                                ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right break-all w-[13rem]">${row.totalCost}</td>` : ``}
                            </tr>
                            `
                }).join('') ?? ''}                    
                    </tbody>
                </table>
            </div>
                `;
            };

            let costGroupRows = [
                ...(result?.[0]?.children ?? []),
                ...(hideCostGroup ? [] : pricing_config?.cost_groups?.map((c) => {

                    const unitCost = c?.costs?.reduce((acc, c) => acc + (c?.total_cost || 0), 0);

                    return {
                        "level": "",
                        "item_number": "N/A",
                        "version": "N/A",
                        "description": c.title,
                        "unitCost": formatCurrency(
                            Number(unitCost.toFixed(2)),
                            base_currency,
                            useMBFormatter,
                            false
                        ),

                        "qty": c.quantity,
                        "totalCost": formatCurrency(
                            Number((c.total_amount ?? 0).toFixed(2)),
                            base_currency,
                            useMBFormatter,
                            false
                        )
                    };
                }) ?? []),
            ];

            if (costGroupRows.length > 0) {
                body += `
            <p class="mt-5 underline" >Items Breakup</p> 
            
            <div>
                <table class="table-fixed mt-5 p-3 pl-6">
                    <thead></thead>

                    <tbody>
                        ${costGroupRows?.map((row, idx) => {
                    return `
                            <tr class="${idx % 2 === 0 ? 'bg-gray-50' : 'bg-gray-100'}">
                                <td class="px-2 py-2 text-md text-left break-all w-[4rem]">${row.level}</td>
                                <td class="px-2 py-2 text-md text-left break-all w-[9rem]">${row.item_number}</td>
                                <td class="px-2 py-2 text-md break-all w-[3rem]">${row.version}</td>
                                <td class="px-2 py-2 text-md text-left break-all w-[16rem]">${row.description?.replaceAll('\n', '<br>') ?? "-"}</td>
                                ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right break-all w-[13rem]">${row.unitCost}</td>` : ``}
                                <td class="px-2 py-2 text-md text-right break-all w-[6rem]">${row.qty}</td>
                                ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right break-all w-[13rem]">${row.totalCost}</td>` : ``}
                            </tr>
                            `
                }).join('') ?? ''}                    
                    </tbody>
                </table>
            </div>`;
            };

            if (commercial && !isNormalized) {
                body += `
                <div>
                    <table class="table-fixed mt-5 p-3 pl-6">
                        <thead id="thead">
                            <tr class=" border-b-2 border-black">
                                <td class="pr-2 text-xl text-left break-all w-[14rem]">Total Cost</td>
                                <td class="pr-2 text-md text-left break-all w-[9rem]"></td>
                                <td class="pr-2 text-md text-left break-all w-[3rem]"></td>
                                ${!(technical || technoCommercial) ? `<td class="pr-2 text-md text-left break-all w-[10rem]"></td>` : ``}
                                ${!(technical || technoCommercial) ? `<td class="pr-2 text-md text-left break-all w-[10rem]"></td>` : ``}
                                <td class="pr-2 text-xl text-right break-all w-[18rem]">${formatCurrency(
                    (bomDetails?.total_bom_cost ?? 0) +
                    (bomDetails?.total_cost_group_cost ?? 0),
                    base_currency,
                    useMBFormatter
                )}</td>
                            </tr>

                            </thead>
                        <tbody></tbody>
                    </table>
                </div>
            `;
            };

            if (pricing_config?.other_costs?.length > 0 && hasOtherCost) {

                let otherCostRows = pricing_config?.other_costs?.map((c, idx) => {
                    return {
                        "level": "",
                        "item_number": idx + 1,
                        "version": "N/A",
                        "description": c.description,
                        "unitCost": "N/A",

                        "qty": c.type === "percentage" ? `${c.value ?? 0}%` : "",
                        "totalCost": formatCurrency(
                            Number((c.amount ?? 0).toFixed(2)),
                            base_currency,
                            useMBFormatter,
                            false
                        ),
                    };
                }) ?? [];

                body += `
            <p class="mt-5 underline" >Other Items</p> 
            
            <div>
                <table class="table-fixed mt-5 p-3 pl-6">
                    <thead></thead>

                    <tbody>
                        ${otherCostRows?.map((row, idx) => {
                    return `
                            <tr class="${idx % 2 === 0 ? 'bg-gray-50' : 'bg-gray-100'}">
                                <td class="px-2 py-2 text-md text-left break-all w-[4rem]">${row.level}</td>
                                <td class="px-2 py-2 text-md text-left break-all w-[9rem]">${row.item_number}</td>
                                <td class="px-2 py-2 text-md break-all w-[3rem]">${row.version}</td>
                                <td class="px-2 py-2 text-md text-left break-all w-[16rem]">${row.description?.replaceAll('\n', '<br>') ?? "-"}</td>
                                ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right break-all w-[13rem]">${row.unitCost}</td>` : ``}
                                <td class="px-2 py-2 text-md text-right break-all w-[6rem]">${row.qty}</td>
                                ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right break-all w-[13rem]">${row.totalCost}</td>` : ``}
                            </tr>
                            `
                }).join('') ?? ''}                    
                    </tbody>
                </table>
            </div>`;

                if (commercial) {
                    body += `
                    <div>
                         <div class="max-w-[${(technical || technoCommercial) ? TOTAL_COST_INITIAL_WIDTH : TOTAL_COST_INITIAL_WIDTH + 26}rem] w-[${(technical || technoCommercial) ? TOTAL_COST_INITIAL_WIDTH : TOTAL_COST_INITIAL_WIDTH + 26}rem] mt-5 flex justify-between border-b-2 border-black">
                            <span class="pr-2 py-2 text-xl text-left break-all">Total Cost (including Other Items)</span>
                            <span class="pr-2 py-2 text-xl text-left break-all">${formatCurrency(
                                bomDetails?.gross_cost ?? 0,
                                base_currency,
                                useMBFormatter
                            )}</span>
                        </div>
                    </div>
                `;
                };
            };

            if (pricing_config?.discounts?.length > 0 && hasAdjustment) {
                let discountRows = pricing_config?.discounts?.map((c, idx) => {
                    return {
                        "level": "",
                        "item_number": idx + 1,
                        "version": "N/A",
                        "description": c.description,
                        "unitCost": "N/A",
                        "qty": c.type === "percentage" ? `${c.value ?? 0}%` : "",
                        "totalCost": formatCurrency(
                            Number((c.amount ?? 0).toFixed(2)),
                            base_currency,
                            useMBFormatter,
                            false
                        )
                    };
                }) ?? [];

                body += `
            <p class="mt-5 underline" >Adjustments</p>
            
            <div>
                <table class="table-fixed mt-5 p-3 pl-6">
                    <thead></thead>

                    <tbody>
                        ${discountRows?.map((row, idx) => {
                    return `
                            <tr class="${idx % 2 === 0 ? 'bg-gray-50' : 'bg-gray-100'}">
                                <td class="px-2 py-2 text-md text-left break-all w-[4rem]">${row.level}</td>
                                <td class="px-2 py-2 text-md text-left break-all w-[9rem]">${row.item_number}</td>
                                <td class="px-2 py-2 text-md break-all w-[3rem]">${row.version}</td>
                                <td class="px-2 py-2 text-md text-left break-all w-[16rem]">${row.description?.replaceAll('\n', '<br>') ?? "-"}</td>
                                ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right break-all w-[13rem]">${row.unitCost}</td>` : ``}
                                <td class="px-2 py-2 text-md text-right break-all w-[6rem]">${row.qty}</td>
                                ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right break-all w-[13rem]">${row.totalCost}</td>` : ``}
                            </tr>
                            `
                }).join('') ?? ''}                    
                    </tbody>
                </table>
            </div>`;

                if (!technical) {
                    body += `
                    <div>
                        <div class="max-w-[${(technical || technoCommercial) ? TOTAL_COST_INITIAL_WIDTH : TOTAL_COST_INITIAL_WIDTH + 26}rem] w-[${(technical || technoCommercial) ? TOTAL_COST_INITIAL_WIDTH : TOTAL_COST_INITIAL_WIDTH + 26}rem] mt-5 flex justify-between bg-[${header_color}] text-[${hexToGrayscale(header_color) > 150 ? "#000000" : "#FFFFFF"}] border-b-2 border-black">
                            <span class="pr-2 py-2 text-xl text-left break-all">Total Cost (including Adjustments)</span>
                            <span class="pr-2 py-2 text-xl text-left break-all">${formatCurrency(
                                bomDetails?.gross_cost_after_discount ?? 0,
                                base_currency,
                                useMBFormatter
                            )}</span>
                        </div>
                    </div>
                `;
                }
            };
        };

        if (!technical && !isNormalized) {
            if (pricing_config?.margin_ratio) {
               body += `
            <p class="mt-5 underline" >Margin</p> 
            
            <div>
                <table class="table-fixed mt-5 p-3 pl-6">
                    <thead></thead>

                    <tbody>
                        <tr class="bg-gray-100">
                                <td class="px-2 py-2 text-md text-left break-all w-[4rem]"></td>
                                <td class="px-2 py-2 text-md text-left break-all w-[9rem]">1</td>
                                <td class="px-2 py-2 text-md break-all w-[3rem]">N/A</td>
                                <td class="px-2 py-2 text-md text-left break-all w-[16rem]">Margin</td>
                                ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right break-all w-[13rem]">N/A</td>` : ``}
                                <td class="px-2 py-2 text-md text-right break-all w-[6rem]">${pricing_config?.margin_ratio}%</td>
                                ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right break-all w-[13rem]">${formatCurrency(
                    bomDetails?.margin_amount ?? 0,
                    base_currency,
                    useMBFormatter,
                    false
                )}</td>` : ``}
                            </tr>                
                    </tbody>
                </table>
        </div>`;

                body += `
                <div>
                    <div class="max-w-[${(technical || technoCommercial) ? TOTAL_COST_INITIAL_WIDTH : TOTAL_COST_INITIAL_WIDTH + 26}rem] w-[${(technical || technoCommercial) ? TOTAL_COST_INITIAL_WIDTH : TOTAL_COST_INITIAL_WIDTH + 26}rem] mt-5 flex justify-between bg-[${header_color}] text-[${hexToGrayscale(header_color) > 150 ? "#000000" : "#FFFFFF"}] border-b-2 border-black">
                        <span class="pr-2 py-2 text-xl text-left break-all">Total Cost (${pricing_config?.margin_ratio ? 'including margin' : ''})</span>
                        <span class="pr-2 py-2 text-xl text-left break-all">${formatCurrency(
                            bomDetails?.gross_cost_after_margin ?? 0,
                            base_currency,
                            useMBFormatter
                        )}</span>
                    </div>
                </div>
            `;
            }
        }

        if (pricing_config?.taxes?.length > 0 && hasTax) {
            const taxes = pricing_config?.taxes?.map((c, idx) => {
                return {
                    "level": "",
                    "item_number": idx + 1,
                    "version": "N/A",
                    "description": c.description,
                    "unitCost": "N/A",
                    "qty": c.type === "percentage" ? `${c.value ?? 0}%` : "",
                    "totalCost": formatCurrency(
                        Number((c.amount ?? 0).toFixed(2)),
                        base_currency,
                        useMBFormatter,
                        false
                    )
                };
            }) ?? [];

            body += `
            <p class="mt-5 underline" >Taxes</p> 
            
            <div>
                <table class="table-fixed mt-5 p-3 pl-6">
                    <thead></thead>

                    <tbody>
                        ${taxes?.map((row, idx) => {
                return `
                            <tr class="${idx % 2 === 0 ? 'bg-gray-50' : 'bg-gray-100'}">
                                <td class="px-2 py-2 text-md text-left break-all w-[4rem]">${row.level}</td>
                                <td class="px-2 py-2 text-md text-left break-all w-[9rem]">${row.item_number}</td>
                                <td class="px-2 py-2 text-md break-all w-[3rem]">${row.version}</td>
                                <td class="px-2 py-2 text-md text-left break-all w-[16rem]">${row.description?.replaceAll('\n', '<br>') ?? "-"}</td>
                                ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right break-all w-[13rem]">${row.unitCost}</td>` : ``}
                                <td class="px-2 py-2 text-md text-right break-all w-[6rem]">${row.qty}</td>
                                ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right break-all w-[13rem]">${row.totalCost}</td>` : ``}
                            </tr>
                            `
            }).join('') ?? ''}                    
                    </tbody>
                </table>
        </div>`;
        };

        if (!technical) {
            body += `
            <div>
                <div class="max-w-[${(technical || technoCommercial) ? TOTAL_COST_INITIAL_WIDTH : TOTAL_COST_INITIAL_WIDTH + 26}rem] w-[${(technical || technoCommercial) ? TOTAL_COST_INITIAL_WIDTH : TOTAL_COST_INITIAL_WIDTH + 26}rem] mt-5 flex justify-between bg-[${header_color}] text-[${hexToGrayscale(header_color) > 150 ? "#000000" : "#FFFFFF"}] border-b-2 border-black">
                    <span class="pr-2 py-2 text-xl text-left break-all">Total Cost (after tax)</span>
                    <span class="pr-2 py-2 text-xl text-left break-all">${formatCurrency(
                        bomDetails?.final_price ?? 0,
                        base_currency,
                        useMBFormatter
                    )}</span>
                </div>
            </div>
        `;
        };

    };

    if (pricing_config?.cost_groups?.length > 0 && hasCGI) {

        body += `<div class="page-break relative">`;

        pricing_config?.cost_groups?.forEach((cg, idx) => {
            body += `<h1 class=" text-2xl font-semibold mt-6">Cost Group - ${cg.title}</h1>`;

            const unitCost = cg?.costs?.reduce((acc, c) => acc + (c?.total_cost || 0), 0);

            body += `
            <div>
                <table class="table-fixed mt-5 p-3 pl-6">
                    <thead>
                        <tr class="bg-[${header_color}] text-[${hexToGrayscale(header_color) > 150 ? "#000000" : "#FFFFFF"}] ">
                            <th class="pl-2 py-2 text-md text-left break-all w-[4rem]">Level</th>
                            <th class="pl-2 py-2 text-md text-left break-all w-[9rem]">Item No.</th>
                            <th class="pl-2 py-2 text-md text-left break-all w-[3rem]">Rev.</th>
                            <th class="pl-2 py-2 text-md text-left break-all w-[16rem]">Description</th>
                            ${!(technical || technoCommercial) ? `<th class="px-2 py-2 text-md text-right break-all w-[13rem]" >Unit Cost (${base_currency})</th>` : ``}                        
                            <th class="px-2 py-2 text-md text-right break-all w-[6rem]">Quantity</th>
                            ${!(technical || technoCommercial) ? `<th class="px-2 py-2 text-md text-right break-all w-[13rem]">Price (${base_currency})</th>` : ``}
                        </tr>
                    </thead>

                    <tbody>
                       <tr class="bg-gray-50">
                            <td class="px-2 py-2 text-md"></td>
                            <td class="px-2 py-2 text-md">N/A</td>
                            <td class="px-2 py-2 text-md">N/A</td>
                            <td class="px-2 py-2 text-md">${cg.title}</td>
                            ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right">${formatCurrency(
                Number(unitCost.toFixed(2)),
                base_currency,
                useMBFormatter,
                false
            )}</td>` : ``}
                            <td class="px-2 py-2 text-md text-right">${cg.quantity}</td>
                            ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right">${formatCurrency(
                Number((cg.total_amount ?? 0).toFixed(2)),
                base_currency,
                useMBFormatter,
                false
            )}</td>` : ``}
                        </tr>                 
                    </tbody>
                </table>
            </div>
            `;

            if (cg.costs?.length > 0) {
                body += `<p class="mt-5 underline">Items Breakup</p>`;

                body += `
            <div>
                <table class="table-fixed mt-5 p-3 pl-6">
                    <thead>
                        <tr class="bg-[${header_color}] text-[${hexToGrayscale(header_color) > 150 ? "#000000" : "#FFFFFF"}] ">
                            <th class="pl-2 py-2 text-md text-left break-all w-[4rem]">Level</th>
                            <th class="pl-2 py-2 text-md text-left break-all w-[9rem]">Item No.</th>
                            <th class="pl-2 py-2 text-md text-left break-all w-[3rem]">Rev.</th>
                            <th class="pl-2 py-2 text-md text-left break-all w-[16rem]">Description</th>
                            ${!(technical || technoCommercial) ? `<th class="px-2 py-2 text-md text-right break-all w-[13rem]" >Unit Cost (${base_currency})</th>` : ``}                        
                            <th class="px-2 py-2 text-md text-right break-all w-[6rem]">Quantity</th>
                            ${!(technical || technoCommercial) ? `<th class="px-2 py-2 text-md text-right break-all w-[13rem]">Price (${base_currency})</th>` : ``}
                        </tr>
                    </thead>

                    <tbody>
                       ${cg.costs?.map((c, idx) => `<tr class="${idx % 2 === 0 ? 'bg-gray-50' : 'bg-gray-100'}" >
                            <td class="px-2 py-2 text-md text-left break-all w-[4rem]"></td>
                            <td class="px-2 py-2 text-md text-left break-all w-[9rem]">N/A</td>
                            <td class="px-2 py-2 text-md text-left break-all w-[3rem]">N/A</td>
                            <td class="px-2 py-2 text-md text-left break-all w-[16rem]">${c.description}</td>
                            ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right break-all w-[16rem]">${formatCurrency(
                    Number(c.value.toFixed(2)),
                    base_currency,
                    useMBFormatter,
                    false
                )}</td>` : ``}
                            <td class="px-2 py-2 text-md text-right break-all w-[6rem]">${c.quantity}</td>
                            ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right break-all w-[16rem]">${formatCurrency(
                    Number((c.total_cost ?? 0).toFixed(2)),
                    base_currency,
                    useMBFormatter,
                    false
                )}</td>` : ``}
                        </tr>`)?.join('') ?? ''}        
                    </tbody>
                </table>
            </div>
                `;
            }
        });

        body += `</div>`;
    };

    if (allLevels) {
        const childLevels = result?.slice(1)?.filter((c) => c.parent?.item_number?.includes("BOM")) ?? [];

        if (childLevels.length > 0) {

            body += `<div class="page-break relative" style="height: auto;">`;

            childLevels?.forEach((r, idx) => {

                body += `
                    <h1 class=" text-2xl font-semibold mt-6">Details Sheet</h1>
                    <p class=" text-neutral-500">
                        L${r.parent?.level ?? ""} - ${r.parent?.description?.split("\nNotes:")?.[0] ?? ""} (${r.parent?.item_number ?? ""}-${r.parent?.version ?? 0})
                    </p>

                    <div>
                        <table class="table-fixed mt-5 p-3 pl-6">
                            <thead>
                                <tr class="bg-[${header_color}] text-[${hexToGrayscale(header_color) > 150 ? "#000000" : "#FFFFFF"}] ">
                                    <th class="pl-2 py-2 text-md text-left break-all w-[4rem]">Level</th>
                                    <th class="pl-2 py-2 text-md text-left break-all w-[9rem]">Item No.</th>
                                    <th class="pl-2 py-2 text-md text-left break-all w-[3rem]">Rev.</th>
                                    <th class="pl-2 py-2 text-md text-left break-all w-[16rem]">Description</th>
                                    ${!(technical || technoCommercial) ? `<th class="px-2 py-2 text-md text-right break-all w-[13rem]" >Unit Cost (${base_currency})</th>` : ``}                        
                                    <th class="px-2 py-2 text-md text-right break-all w-[6rem]">Quantity</th>
                                    ${!(technical || technoCommercial) ? `<th class="px-2 py-2 text-md text-right break-all w-[13rem]">Price (${base_currency})</th>` : ``}
                                </tr>
                            </thead>

                            <tbody>
                            <tr class="bg-gray-50">
                                    <td class="px-2 py-2 text-md">${r?.parent?.level ?? ""}</td>
                                    <td class="px-2 py-2 text-md">${r?.parent?.item_number ?? "N/A"}</td>
                                    <td class="px-2 py-2 text-md">${r?.parent?.version ?? "N/A"}</td>
                                    <td class="px-2 py-2 text-md">${r?.parent?.description ?? "N/A"}</td>
                                    ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right">${r?.parent?.unitCost ?? 0}</td>` : ``}
                                    <td class="px-2 py-2 text-md text-right">${r?.parent?.qty ?? ""}</td>
                                    ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right">${r?.parent?.totalCost ?? 0}</td>` : ``}
                                </tr>                 
                            </tbody>
                        </table>
                    </div>
                `;

                if (r.assembly_process_costs?.length > 0) {
                    body += `<p class="mt-5 underline">Overhead / Activity Breakup</p>`;

                    body += `
                    <div>
                        <table class="table-fixed mt-5 p-3 pl-6">
                            <thead></thead>

                            <tbody>
                                ${r.assembly_process_costs?.map((_: any, _idx: number) => `<tr class="${_idx % 2 === 0 ? 'bg-gray-50' : 'bg-gray-100'}" >
                                    <td class="px-2 py-2 text-md text-left break-all w-[4rem]">${_?.level ?? ""}</td>
                                    <td class="px-2 py-2 text-md text-left break-all w-[9rem]">${_?.item_number ?? "N/A"}</td>
                                    <td class="px-2 py-2 text-md text-left break-all w-[3rem]">${_?.version ?? "N/A"}</td>
                                    <td class="px-2 py-2 text-md text-left break-all w-[16rem]">${_?.description ?? "N/A"}</td>
                                    ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right break-all w-[13rem]">${_?.unitCost ?? 0}</td>` : ``}
                                    <td class="px-2 py-2 text-md text-right break-all w-[6rem]">${_.qty}</td>
                                    ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right break-all w-[13rem]">${_?.totalCost ?? 0}</td>` : ``}
                                </tr>`)?.join('') ?? ''}        
                            </tbody>
                        </table>
                    </div>
                    `;
                };

                if (r.children?.length > 0) {
                    body += `<p class="mt-5 underline">Items Breakup</p>`;

                    body += `
                    <div>
                        <table class="table-fixed mt-5 p-3 pl-6">
                            <thead></thead>

                            <tbody>
                                ${r.children?.map((_: any, _idx: number) => `<tr class="${_idx % 2 === 0 ? 'bg-gray-50' : 'bg-gray-100'}" >
                                    <td class="px-2 py-2 text-md text-left break-all w-[4rem]">${_?.level ?? ""}</td>
                                    <td class="px-2 py-2 text-md text-left break-all w-[9rem]">${_?.item_number ?? "N/A"}</td>
                                    <td class="px-2 py-2 text-md text-left break-all w-[3rem]">${_?.version ?? "N/A"}</td>
                                    <td class="px-2 py-2 text-md text-left break-all w-[16rem]">${_?.description ?? "N/A"}</td>
                                    ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right break-all w-[13rem]">${_?.unitCost ?? 0}</td>` : ``}
                                    <td class="px-2 py-2 text-md text-right break-all w-[6rem]">${_.qty}</td>
                                    ${!(technical || technoCommercial) ? `<td class="px-2 py-2 text-md text-right break-all w-[13rem]">${_?.totalCost ?? 0}</td>` : ``}
                                </tr>`)?.join('') ?? ''}        
                            </tbody>
                        </table>
                    </div>
                    `;
                };

                if (commercial) {
                    body += `
                     <div>
                        <table class="table-fixed mt-5 p-3 pl-6">
                            <thead id="thead">
                                <tr class=" bg-[${header_color}] text-[${hexToGrayscale(header_color) > 150 ? "#000000" : "#FFFFFF"}] border-b-2 border-black">
                                    <td class="pr-2 py-2 text-xl text-left break-all w-[25rem]">Total Cost</td>
                                    <td class="pr-2 py-2 text-md text-left break-all w-[3rem]"></td>
                                    ${!(technical || technoCommercial) ? `<td class="pr-2 py-2 text-md text-left break-all w-[10rem]"></td>` : ``}
                                    ${!(technical || technoCommercial) ? `<td class="pr-2 py-2 text-md text-left break-all w-[10rem]"></td>` : ``}
                                    <td class="pr-2 py-2 text-xl text-right break-all w-[16rem]">${formatCurrency(r.totalParentCost, base_currency, useMBFormatter)}</td>
                                </tr>
                                </thead>
                            <tbody></tbody>
                        </table>
                    </div>`;
                };
            });

            body += `</div>`;
        };
    };

    return Promise.resolve({ body, script: buildScript(functionNames, functionDefinitions) });
};